/**
 * userPreferences.js
 * Lightweight, privacy-respecting client interaction signals tracker.
 * Stores category interests, search queries, and recent browsing in localStorage
 * to provide a subtle, seamless personalization boost for category recommendations.
 */

const STORAGE_KEY = 'im_user_category_signals';
const MAX_SIGNALS = 20;

export const recordCategoryInteraction = (categoryNameOrSlug, weight = 1) => {
    if (!categoryNameOrSlug || typeof window === 'undefined') return;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const signals = raw ? JSON.parse(raw) : {};

        const key = String(categoryNameOrSlug).toLowerCase().trim();
        const current = signals[key] || { count: 0, lastUpdated: Date.now() };

        signals[key] = {
            count: (current.count || 0) + weight,
            lastUpdated: Date.now()
        };

        // Keep size bounded
        const keys = Object.keys(signals);
        if (keys.length > MAX_SIGNALS) {
            const sorted = keys.sort((a, b) => signals[b].lastUpdated - signals[a].lastUpdated);
            const trimmed = {};
            sorted.slice(0, MAX_SIGNALS).forEach(k => {
                trimmed[k] = signals[k];
            });
            localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(signals));
        }
    } catch (e) {
        // Silently ignore storage errors in incognito/restricted mode
    }
};

export const getTopUserCategories = () => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const signals = JSON.parse(raw);

        return Object.keys(signals)
            .sort((a, b) => (signals[b].count || 0) - (signals[a].count || 0));
    } catch (e) {
        return [];
    }
};

export const rankCategoriesByInterest = (categories = []) => {
    const topKeys = getTopUserCategories();
    if (!topKeys.length || !categories.length) return categories;

    // Create a copy to sort
    return [...categories].sort((a, b) => {
        const aName = (a.name || a.id || '').toLowerCase();
        const bName = (b.name || b.id || '').toLowerCase();

        const aIndex = topKeys.findIndex(k => aName.includes(k) || k.includes(aName));
        const bIndex = topKeys.findIndex(k => bName.includes(k) || k.includes(bName));

        // If user interacted with a, boost it
        if (aIndex !== -1 && bIndex === -1) return -1;
        if (bIndex !== -1 && aIndex === -1) return 1;
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;

        // Otherwise preserve original displayOrder / featured flag
        return (a.displayOrder || 99) - (b.displayOrder || 99);
    });
};
